import {
    APP_FLAVOR_USER,
    getPostLoginFlavor,
} from "./appFlavor";

export const DASHBOARD_ROOT = "/dashboard";

const parseSessionBool = (key) => {
    const value = window.sessionStorage.getItem(key);
    if (!value) return false;
    try {
        return JSON.parse(value);
    } catch {
        return value === "true";
    }
};

export const emptyAuthState = () => ({
    access_token: "",
    username: "",
    id: "",
    usertype: "",
    usercompany: "",
    first_name: "",
    last_name: "",
    company_admin: false,
    is_superuser: false,
    package: {},
    sidebar_config: {},
    email: "",
});

export const loadAuthFromSession = () => {
    const access_token = window.sessionStorage.getItem("auth");
    const id = window.sessionStorage.getItem("id");
    if (!access_token || !id) {
        return null;
    }

    try {
        const packageRaw = window.sessionStorage.getItem("package");
        const sidebarRaw = window.sessionStorage.getItem("sidebar_config");
        return {
            access_token,
            username: window.sessionStorage.getItem("username") || "",
            id: Number(id) || "",
            usertype: Number(window.sessionStorage.getItem("usertype")) || "",
            usercompany: window.sessionStorage.getItem("usercompany") || "",
            first_name: window.sessionStorage.getItem("first_name") || "",
            last_name: window.sessionStorage.getItem("last_name") || "",
            company_admin: parseSessionBool("company_admin"),
            is_superuser: parseSessionBool("is_superuser"),
            package: packageRaw ? JSON.parse(packageRaw) : {},
            sidebar_config: sidebarRaw ? JSON.parse(sidebarRaw) : {},
            email: window.sessionStorage.getItem("email") || "",
        };
    } catch {
        return null;
    }
};

export const isAuthenticated = (auth) =>
    Boolean(auth?.access_token && auth?.id);

export const clearAuthSession = () => {
    [
        "auth",
        "username",
        "id",
        "usertype",
        "usercompany",
        "first_name",
        "last_name",
        "package",
        "sidebar_config",
        "company_admin",
        "is_superuser",
        "email",
    ].forEach((key) => window.sessionStorage.removeItem(key));
    window.localStorage.removeItem("refresh_token");
};

export const getLoginErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (!data) {
        return error?.message || fallback;
    }
    if (typeof data.error_description === "string") {
        return data.error_description;
    }
    if (typeof data.detail === "string") {
        return data.detail;
    }
    if (typeof data.error === "string") {
        return data.error;
    }
    return fallback;
};

export const persistAuthSession = (tokenResponse) => {
    const user = tokenResponse.user || {};
    window.sessionStorage.auth = tokenResponse.access_token;
    window.sessionStorage.id = String(user.id);
    window.sessionStorage.username = user.username || "";
    window.sessionStorage.usertype = user.user_level;
    window.sessionStorage.usercompany = user.company ?? "";
    window.sessionStorage.first_name = user.first_name || "";
    window.sessionStorage.last_name = user.last_name || "";
    window.sessionStorage.package = JSON.stringify(user.package || {});
    window.sessionStorage.sidebar_config = JSON.stringify(
        user.sidebar_config || {}
    );
    window.sessionStorage.company_admin = JSON.stringify(user.company_admin);
    window.sessionStorage.is_superuser = JSON.stringify(user.is_superuser);
    window.sessionStorage.email = user.email || "";
};

export const buildAuthState = (auth, tokenResponse) => {
    const user = tokenResponse.user || {};
    return {
        ...auth,
        id: user.id,
        access_token: tokenResponse.access_token,
        username: user.username,
        usertype: user.user_level,
        usercompany: user.company,
        first_name: user.first_name,
        last_name: user.last_name,
        package: user.package || {},
        sidebar_config: user.sidebar_config || {},
        company_admin: user.company_admin,
        is_superuser: user.is_superuser,
        email: user.email,
    };
};

export const applyTokenResponse = (setAuth, tokenResponse) => {
    if (!tokenResponse?.access_token || !tokenResponse?.user?.id) {
        throw new Error("Invalid login response");
    }
    if (tokenResponse.refresh_token) {
        window.localStorage.refresh_token = tokenResponse.refresh_token;
    }
    persistAuthSession(tokenResponse);
    setAuth((prev) => buildAuthState(prev, tokenResponse));
    return tokenResponse;
};

export const getDefaultPanelPath = (auth, flavor) => {
    if (!isAuthenticated(auth)) {
        return DASHBOARD_ROOT;
    }
    const resolvedFlavor = getPostLoginFlavor(auth, flavor);
    if (resolvedFlavor === APP_FLAVOR_USER) {
        return `${DASHBOARD_ROOT}/user/dashboard`;
    }
    if (Number(auth.usertype) === 1 || auth.is_superuser) {
        return `${DASHBOARD_ROOT}/organizations`;
    }
    // Company users (admin or regular members) stay in the organization panel.
    if (Number(auth.usertype) === 2 && auth.usercompany) {
        if (auth.company_admin) {
            return `${DASHBOARD_ROOT}/profile`;
        }
        return `${DASHBOARD_ROOT}/messages/organ`;
    }
    return `${DASHBOARD_ROOT}/user/dashboard`;
};

export const getPostLoginPath = (user, flavor) => {
    const resolvedFlavor = getPostLoginFlavor(user, flavor);
    if (resolvedFlavor === APP_FLAVOR_USER) {
        return `${DASHBOARD_ROOT}/user/dashboard`;
    }
    if (user?.is_superuser || Number(user?.user_level) === 1) {
        return `${DASHBOARD_ROOT}/organizations`;
    }
    // Real org members open the same organization panel as non-admins.
    if (Number(user?.user_level) === 2 && user?.company) {
        if (user?.company_admin) {
            return `${DASHBOARD_ROOT}/profile`;
        }
        return `${DASHBOARD_ROOT}/messages/organ`;
    }
    return `${DASHBOARD_ROOT}/user/dashboard`;
};

/** Relative in-app paths only; blocks open redirects and the auth page itself. */
export const isSafeReturnPath = (path) =>
    typeof path === "string" &&
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.startsWith("/auth");

export const resolvePostLoginPath = (user, returnPath, flavor) => {
    if (isSafeReturnPath(returnPath)) {
        return returnPath;
    }
    return getPostLoginPath(user, flavor);
};
